import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';
import { useAuth } from '../contexts/AuthContext';
import { QuickExport } from '../components/settings/QuickExport';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect viewers to their dedicated page
  const isViewer = user?.role_name?.toLowerCase() === 'viewer';

  const isStaff = user?.role_name?.toLowerCase() === 'staff';
  const isAdmin = user?.role_name?.toLowerCase() === 'admin';
  useEffect(() => {
    if (isAuthenticated && isStaff) navigate('/staff', { replace: true });
    if (isAuthenticated && isViewer) {
      navigate('/prepare/upcoming-services', { replace: true });
    }
  }, [isAuthenticated, isViewer, isStaff, navigate]);

  // Show loading while checking auth/redirecting
  if (isLoading || (isAuthenticated && (isViewer || isStaff))) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <Navigation />
      <main className="home-content">
        <div className="hero-section">
          <h1 className="hero-title">Liturgical Music</h1>
          {isAuthenticated ? (
            <p className="hero-subtitle">
              Welcome back, {user?.first_name || user?.username}! 
              {user?.church_name && <span> ({user.church_name})</span>}
            </p>
          ) : (
            <p className="hero-subtitle">
              Manage your church's music planning and song selections
            </p>
          )}
        </div>


        <Link to="/music-ministry" className="music-vision-entry">
          <span className="music-vision-entry-label">Our Musical Life</span>
          <span className="music-vision-entry-copy">
            Read our music vision, mission, and the practices that connect every
            ministry to the congregation’s song.
          </span>
          <span className="music-vision-entry-action">Explore the vision →</span>
        </Link>

        {isAuthenticated ? (
          <div className="features-grid">
            <Link to="/planning" className="feature-card">
              <h3>Planning</h3>
              <p>Organize and plan music for your church services</p>
            </Link>
            {isAdmin && (
              <div className="feature-card indesign-card">
                <Link to="/indesign" className="indesign-card-title"><h3>InDesign Export</h3></Link>
                <QuickExport />
                <Link to="/indesign" className="indesign-card-more">Choose another Sunday or customize →</Link>
              </div>
            )}
            <Link to="/songs" className="feature-card">
              <h3>Songs</h3>
              <p>Browse and manage your complete song library</p>
            </Link>
            <Link to="/choir" className="feature-card">
              <h3>Choir</h3>
              <p>View upcoming services with sheet music and practice videos</p>
            </Link>
            <Link to="/settings" className="feature-card">
              <h3>Settings</h3>
              <p>Configure templates and manage your music catalog</p>
            </Link>
          </div>
        ) : (
          <div className="auth-section">
            <div className="auth-cards">
              <div className="auth-card">
                <h3>Already have an account?</h3>
                <p>Sign in to access your church's music planning tools</p>
                <Link to="/login" className="btn btn-primary">Login</Link>
              </div>
              <div className="auth-card">
                <h3>New to our platform?</h3>
                <p>Create an account to start managing your church's music</p>
                <Link to="/signup" className="btn btn-outline-primary">Sign Up</Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HomePage;
